/**
 * FloorHeading — заголовок этажа и объяснение, что это за этаж.
 *
 * Собственный, а не `Section` из дизайн-системы: `SectionHeader` жёстко
 * набран `heading.m` (18px), а этажам отчёта нужен вес крупнее — они
 * делят документ на 7 минут чтения, а не подписывают карточку.
 *
 * Объяснение стоит текстом, а не за значком вопроса. Подсказка по наведению
 * предполагает, что читатель уже знает, чего не понимает; здесь смысл блока
 * сообщается до того, как он упрётся в цифры.
 *
 * @format
 */

import {type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {MEASURE_WIDE} from '../helpers';

export interface FloorHeadingProps {
  title: string;
  /** Что это за блок и зачем он тут. Одно-два предложения. */
  description?: string;
  /** Бейджи справа от заголовка — сумма, счётчик и т.п. */
  badges?: ReactNode;
  /**
   * Блок справа от всей связки «заголовок + объяснение» — вклад этажа в
   * оценку. Правее заголовка, а не под ним: это не часть объяснения, а
   * отдельное измерение того же раздела, и место у него своё.
   */
  aside?: ReactNode;
  children?: ReactNode;
}

const Title = styled(Box)({
  ...text('heading.xl'),
  color: surfaceVar('onSurface'),
  // Заголовок рендерится как <h2>, а браузер даёт ему собственные поля
  // 0.83em — при кегле 32px это 27px сверху и снизу. Они и создавали
  // разрыв: gap контейнера при этом честно стоял на нуле.
  margin: 0,
});

const Description = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE_WIDE,
});

/**
 * Заголовок с пояснением — одна связка, и разрыв внутри неё меньше, чем
 * разрыв до контента. Иначе пояснение зрительно отрывается от заголовка и
 * прилипает к первой карточке, объясняя вместо этажа её.
 */
const Content = styled(Box)({
  marginTop: spacingPx('xl'),
});

/**
 * Колонка заголовка рядом с блоком справа.
 *
 * `minWidth: 0` обязателен: без него флекс-элемент не сжимается ниже своего
 * содержимого, и длинное слово в заголовке вытолкнуло бы соседний блок за
 * край полосы.
 */
const HeadingColumn = styled(Box)({
  flex: '1 1 auto',
  minWidth: 0,
});

export function FloorHeading({title, description, badges, aside, children}: FloorHeadingProps) {
  const heading = (
    // Разрыв внутри связки «заголовок + пояснение» ровно вдвое меньше
    // разрыва до контента: так видно, что пояснение принадлежит
    // заголовку, а не следующему блоку.
    <Stack gap='m'>
      <Row gap='s-plus' alignY='center' wrap='wrap'>
        <Title as='h2'>{title}</Title>
        {badges}
      </Row>
      {description && <Description>{description}</Description>}
    </Stack>
  );

  return (
    <Stack gap='none'>
      {aside ? (
        // Верх по верху, а не по центру: заголовок и блок справа начинаются
        // с одной линии, и связь между ними читается сразу. Центрирование
        // на длинном объяснении увело бы блок к середине абзаца, где он
        // выглядел бы примечанием к его середине.
        <Row gap='l' alignY='start' justify='between' reflowAt='lg'>
          <HeadingColumn>{heading}</HeadingColumn>
          {aside}
        </Row>
      ) : (
        heading
      )}
      {children && <Content>{children}</Content>}
    </Stack>
  );
}
